import { useState, useEffect } from 'react';
import './installment.css';
import { toast } from 'react-toastify';

export default function Installment({ packData, setPackData, index, customPrice }) {
    const [instalments, setInstalments] = useState(packData.price[index]?.instalments);
    const [isInstallmentChecked, setIsInstallmentChecked] = useState(false);

    useEffect(() => {
        const existing = packData.price[index]?.instalments;
        if (Array.isArray(existing) && existing.length > 0) {
            setInstalments(existing);
            setIsInstallmentChecked(true);
        }
    }, [index, packData.price[index]?.instalments]); // Don't include packData here if it causes constant reruns

    const handleCheckboxChange = (event) => {
        const checked = event.target.checked;
        const totalPrice = customPrice
            ? packData.price[index]?.customPrice?.groupPrice
            : packData.price[index]?.price?.actual;

        if (checked) {
            if (!totalPrice || totalPrice === 0) {
                customPrice
                    ? toast.warning('Please add group price to enable installments.')
                    : toast.warning('Please add actual price to enable installments.');
                return;
            }

            if (totalPrice < 10000) {
                toast.warning('You can add installments only if price is greater than 10,000.');
                return;
            }
            setIsInstallmentChecked(true);
            setInstalments([]);

            // Update packData to include blank installment
            setPackData((prevState) => {
                const updatedPriceArray = prevState.price.map((item, idx) => {
                    if (idx === index) {
                        return {
                            ...item,
                            instalments: []
                        };
                    }
                    return item;
                });

                return {
                    ...prevState,
                    price: updatedPriceArray
                };
            });
        } else {
            setIsInstallmentChecked(false);
            setInstalments([]);

            // Clear installments from packData
            setPackData((prevState) => {
                const updatedPriceArray = prevState.price.map((item, idx) => {
                    if (idx === index) {
                        return {
                            ...item,
                            instalments: []
                        };
                    }
                    return item;
                });

                return {
                    ...prevState,
                    price: updatedPriceArray
                };
            });
        }
    };

    const handleInstallmentCountChange = (count) => {
        const newInstallments = Array(count)
            .fill(null)
            .map(() => ({
                percentage: 0,
                amount: 0,
                comment: ''
            }));
        setInstalments(newInstallments);
    };

    const handleInstallmentChange = (i, name, value) => {
        const totalPrice = customPrice
            ? packData.price[index]?.customPrice?.groupPrice
            : packData.price[index]?.price?.total || 0;

        setInstalments((prev) => {
            const updated = [...prev];
            const updatedValue = name === 'percentage' ? parseFloat(value) || 0 : value;

            if (name === 'percentage') {
                if (updatedValue <= 0) {
                    toast.warning(`Installment ${i + 1}: Percentage must be greater than 0%`);
                    return prev;
                }

                if (updatedValue === 100 && i === 0) {
                    toast.warning(`Installment ${i + 1}: Percentage can't be 100%`);
                    return prev;
                }

                if (updatedValue > 100) {
                    toast.warning(`Installment ${i + 1}: Percentage cannot exceed 100%`);
                    return prev;
                }

                // Temporarily assign the new value for calculation
                updated[i] = {
                    ...updated[i],
                    [name]: updatedValue
                };

                // Calculate total percentage excluding the last installment (if current isn't last)
                const isNotLast = i !== updated.length - 1;
                let totalPercentage = 0;
                for (let j = 0; j < updated.length - 1; j++) {
                    const p = parseFloat(updated[j].percentage);
                    totalPercentage += !isNaN(p) ? p : 0;
                }

                // Check for over 100% before applying to last installment
                if (isNotLast && totalPercentage > 100) {
                    toast.warning('Total percentage cannot exceed 100%');
                    return prev;
                }

                // Auto-calculate amount
                updated[i].amount = (totalPrice * updatedValue) / 100;

                // Auto-fill last installment percentage and amount
                if (isNotLast) {
                    const remaining = 100 - totalPercentage;

                    if (remaining >= 0) {
                        updated[updated.length - 1].percentage = remaining;
                        updated[updated.length - 1].amount = (totalPrice * remaining) / 100;
                    }
                }
            } else {
                updated[i] = {
                    ...updated[i],
                    [name]: updatedValue
                };
            }

            // Save to packData automatically
            setPackData((prevState) => {
                const updatedArray = prevState.price.map((item, itemKey) => {
                    if (index === itemKey) {
                        return {
                            ...item,
                            instalments: updated
                        };
                    }
                    return item;
                });
                return { ...prevState, price: updatedArray };
            });

            return updated;
        });
    };

    // Sync with packData when installment updates
    useEffect(() => {
        if (!isInstallmentChecked) return;

        setPackData((prevState) => {
            const updatedArray = prevState.price.map((item, itemKey) => {
                if (index === itemKey) {
                    return {
                        ...item,
                        instalments: instalments
                    };
                }
                return item;
            });
            return { ...prevState, price: updatedArray };
        });
    }, [instalments]);

    return (
        <div>
            <div className='checkbox-wrapper'>
                <input
                    type='checkbox'
                    id={`customCheck-${index}`} // unique ID per item
                    checked={isInstallmentChecked}
                    onChange={handleCheckboxChange}
                />
                <label htmlFor={`customCheck-${index}`} className='itineraryLabel'>
                    Add installment option
                </label>
            </div>

            {isInstallmentChecked && (
                <div className='itineraryInDiv'>
                    <label className='itineraryLabel'>Number of Installments</label>
                    <div className='itineraryInputDiv'>
                        <select
                            className='itineraryInput'
                            defaultValue=''
                            onChange={(e) => {
                                const count = parseInt(e.target.value, 10);
                                if (count >= 2 && count <= 10) {
                                    handleInstallmentCountChange(count);
                                }
                            }}
                        >
                            <option value='' disabled>
                                Select number of installments
                            </option>
                            {Array.from({ length: 9 }, (_, i) => i + 2).map((num) => (
                                <option key={num} value={num}>
                                    {num}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {isInstallmentChecked &&
                instalments.map((inst, i) => (
                    <div key={i} className='installmentDiv'>
                        <p className='itineraryLabel orgPackUpH2'>Installment {i + 1}</p>

                        <div className='itineraryInDiv'>
                            <label className='itineraryLabel'>Percentage</label>
                            <div className='itineraryInputDiv'>
                                <input
                                    value={inst.percentage}
                                    type='number'
                                    name='percentage'
                                    onChange={(e) => handleInstallmentChange(i, e.target.name, e.target.value)}
                                    placeholder={`Enter installment ${i + 1} percentage`}
                                    className='itineraryInput'
                                    readOnly={i === instalments.length - 1}
                                />
                            </div>
                        </div>

                        <div className='itineraryInDiv'>
                            <label className='itineraryLabel'>Comment</label>
                            <div className='itineraryInputDiv'>
                                <input
                                    value={inst.comment}
                                    type='text'
                                    name='comment'
                                    onChange={(e) => handleInstallmentChange(i, e.target.name, e.target.value)}
                                    placeholder={`Comment for installment ${i + 1}`}
                                    className='itineraryInput'
                                />
                            </div>
                        </div>

                        <div className='itineraryInDiv'>
                            <label className='itineraryLabel'>Amount</label>
                            <div className='itineraryInputDiv'>
                                <input
                                    value={inst.amount}
                                    type='number'
                                    name='amount'
                                    readOnly
                                    placeholder={`Amount for installment ${i + 1}`}
                                    className='itineraryInput'
                                />
                            </div>
                        </div>
                    </div>
                ))}
        </div>
    );
}
